import type { ReactNode } from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useComponentPage } from './ComponentPageContext';
import type { RelatedEntity, RelationVia } from './relations';
import { UnresolvedBadge } from './UnresolvedBadge';

const useStyles = makeStyles(theme => ({
  // Fixed layout: the columns share the card's width instead of the longest
  // name setting it, so the table never runs past the card's edge.
  table: { tableLayout: 'fixed', width: '100%' },
  cell: {
    padding: theme.spacing(0.75, 1.25),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  muted: { color: 'var(--ui-text-muted)' },
  unresolvedName: {
    display: 'inline-flex',
    alignItems: 'baseline',
    maxWidth: '100%',
  },
  ref: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  compactList: { listStyle: 'none', margin: 0, padding: 0 },
  compactRow: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
    '&:last-child': { borderBottom: 'none' },
  },
  compactHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  compactMeta: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
}));

type Direction = 'out' | 'in' | 'part';

// A table row reads "Conductor — depends on it", so the words follow the
// section, unlike the topology's edges, which read left to right.
const RELATION_LABEL: Record<
  Direction,
  Partial<Record<RelationVia, string>>
> = {
  out: { dependsOn: 'depends on', consumesApi: 'consumes API' },
  in: { dependencyOf: 'depends on it', callsItsApi: 'consumes its API' },
  part: {},
};

const relationOf = (e: RelatedEntity, direction: Direction) =>
  e.via
    .map(v => RELATION_LABEL[direction][v])
    .filter(Boolean)
    .join(' · ');

const kindOf = (e: RelatedEntity) =>
  [e.kind.toLowerCase(), e.resolved ? e.type : undefined]
    .filter(Boolean)
    .join(' · ');

const Name = ({ entity: e }: { entity: RelatedEntity }) => {
  const classes = useStyles();
  if (!e.resolved) {
    return (
      // Its name, as the topology shows it, with the full reference on hover;
      // the name is what gives way when the cell is narrow, never the badge.
      <span className={classes.unresolvedName}>
        <span className={classes.ref} title={`${e.ref} is not in the catalog`}>
          {e.name}
        </span>
        <UnresolvedBadge />
      </span>
    );
  }
  // Named by what the catalog already returned, so the row does not start on
  // the bare name and change to the title a moment later.
  return (
    <EntityRefLink entityRef={e.ref} disableTooltip>
      {e.title ?? e.name}
    </EntityRefLink>
  );
};

// An unresolved entry has no owner or lifecycle to show, and says why rather
// than leaving the cell empty.
const Owner = ({ entity: e }: { entity: RelatedEntity }) => {
  const classes = useStyles();
  if (!e.resolved)
    return <span className={classes.muted}>Not in the catalog</span>;
  if (!e.owner) return <span className={classes.muted}>No owner</span>;
  return (
    <EntityRefLink entityRef={e.owner} defaultKind="group" disableTooltip />
  );
};

const lifecycleOf = (e: RelatedEntity) => {
  if (!e.resolved) return 'Unknown';
  return e.lifecycle ?? 'Not set';
};

const RelationList = ({
  label,
  entities,
  direction,
}: {
  label: string;
  entities: RelatedEntity[];
  direction: Direction;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Below ~600 px a row becomes two lines: the name and its lifecycle above,
  // the rest small beneath (the ticket's rule for every table on the page).
  const narrow = useMediaQuery(theme.breakpoints.down('xs'));
  const withRelation = direction !== 'part';

  if (narrow) {
    return (
      <ul className={classes.compactList} aria-label={label}>
        {entities.map(e => (
          <li
            key={e.ref}
            className={classes.compactRow}
            data-unresolved={e.resolved ? undefined : 'true'}
          >
            <div className={classes.compactHead}>
              <span style={{ minWidth: 0 }}>
                <Name entity={e} />
              </span>
              <span className={classes.compactMeta}>{lifecycleOf(e)}</span>
            </div>
            <div className={classes.compactMeta}>
              {kindOf(e)}
              {withRelation && ` · ${relationOf(e, direction)}`} ·{' '}
              <Owner entity={e} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  const columns = ['Name', 'Kind', 'Owner', 'Lifecycle'];
  if (withRelation) columns.push('Relation');
  return (
    <Table size="small" className={classes.table} aria-label={label}>
      <colgroup>
        <col style={{ width: withRelation ? '25%' : '36%' }} />
        <col style={{ width: withRelation ? '17%' : '20%' }} />
        <col style={{ width: withRelation ? '18%' : '26%' }} />
        <col style={{ width: withRelation ? '12%' : '18%' }} />
        {withRelation && <col style={{ width: '28%' }} />}
      </colgroup>
      <TableHead>
        <TableRow>
          {columns.map(title => (
            <TableCell key={title} className={classes.cell}>
              {title}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {entities.map(e => (
          <TableRow
            key={e.ref}
            data-unresolved={e.resolved ? undefined : 'true'}
          >
            <TableCell className={classes.cell}>
              <Name entity={e} />
            </TableCell>
            <TableCell className={classes.cell}>{kindOf(e)}</TableCell>
            <TableCell className={classes.cell}>
              <Owner entity={e} />
            </TableCell>
            <TableCell className={classes.cell}>{lifecycleOf(e)}</TableCell>
            {withRelation && (
              // The whole relation on hover, where a narrow table cuts it.
              <TableCell
                className={classes.cell}
                title={relationOf(e, direction)}
              >
                {relationOf(e, direction)}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const Section = ({
  title,
  subheader,
  entities,
  direction,
  howToDeclare,
}: {
  title: string;
  subheader: string;
  entities: RelatedEntity[];
  direction: Direction;
  howToDeclare?: ReactNode;
}) => (
  <InfoCard
    title={title}
    subheader={subheader}
    titleTypographyProps={{ component: 'h2' }}
  >
    {entities.length ? (
      <RelationList label={title} entities={entities} direction={direction} />
    ) : (
      <>
        <Typography>None declared</Typography>
        {howToDeclare && (
          <Typography variant="body2" color="textSecondary">
            {howToDeclare}
          </Typography>
        )}
      </>
    )}
  </InfoCard>
);

/**
 * A component's Dependencies tab (specs/component-page.md): what it depends on,
 * what depends on it, and — only when there are any — its subcomponents. The
 * relations are the ones the topology draws and the tab counts; one reading
 * for all three, so they cannot disagree.
 */
export const DependenciesContent = () => {
  const { catalog } = useComponentPage();

  if (catalog.loading) return <Progress />;
  if (catalog.error || !catalog.relations) {
    return (
      <InfoCard title="Dependencies">
        <Typography role="alert">
          Couldn't load the dependencies from the catalog.
        </Typography>
      </InfoCard>
    );
  }

  const { outbound, inbound, subcomponents } = catalog.relations;
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Section
          title="Depends on"
          subheader="The components and resources it needs, and the APIs it calls"
          entities={outbound}
          direction="out"
          howToDeclare={
            <>
              Declare them under <code>dependsOn</code> and{' '}
              <code>consumesApis</code> in this component's{' '}
              <code>catalog-info.yaml</code>.
            </>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Section
          title="Depended on by"
          subheader="Who depends on it, and who calls the APIs it provides"
          entities={inbound}
          direction="in"
          howToDeclare={
            <>
              A component appears here once its own{' '}
              <code>catalog-info.yaml</code> names this one under{' '}
              <code>dependsOn</code>, or one of its APIs under{' '}
              <code>consumesApis</code>.
            </>
          }
        />
      </Grid>
      {subcomponents.length > 0 && (
        <Grid item xs={12}>
          <Section
            title="Subcomponents"
            subheader="Components declared as part of this one"
            entities={subcomponents}
            direction="part"
          />
        </Grid>
      )}
    </Grid>
  );
};
